import { Suspense, lazy, useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useSystemStore } from '../store/systemStore';
import { useBrowserContext } from '../hooks/useBrowserContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import EnvironmentBadge from '../components/EnvironmentBadge';
import PasswordInput from '../components/auth/PasswordInput';
import api from '../services/api';
import { asApiError } from '../lib/apiError';
import { serverFailureMessage } from '../lib/serverFailure';
import { throttleMessage } from '../lib/throttle';
import { safeNextPath } from '../lib/safeNext';
import type { TwoFactorLoginResult } from '../components/auth/TwoFactorChallenge';
import type { User } from '../store/authStore';
import { afterLoginPath } from '../lib/twoFactor';

import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/ToastProvider';
import WebsiteLogo from '@/components/auth/WebsiteLogo';

// T3108: korak dvostopenjske prijave rabi le del uporabnikov — naloži se po geslu.
const TwoFactorChallenge = lazy(() =>
  import('../components/auth/TwoFactorChallenge').then((m) => ({ default: m.TwoFactorChallenge })),
);

const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [showResend, setShowResend] = useState(false);
  // T2816: vmesni žeton po uspešnem geslu, ko ima račun dvostopenjsko prijavo.
  const [twoFactorToken, setTwoFactorToken] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const { login, isAuthenticated, user: currentUser } = useAuthStore();
  const toastCtx = useToast();
  const { settings } = useSystemStore();

  const isRegistrationOpen = settings?.is_registration_open ?? true;
  const regClosed = searchParams.get('reg_closed') === '1';

  // T1224: javna tržnica pošlje popisovalca na `/login?next=/marketplace?job=5`.
  // Do zdaj tega parametra ni bral **nihče** — `seo.login_url()` ga je gradil od
  // M11, prijava pa je vedno peljala na `/`, zato je bila celotna obljuba o
  // vračilu na ciljno stran mrtva. `safeNextPath` zapre odprto preusmeritev.
  const nextPath = safeNextPath(searchParams.get('next'));

  useBrowserContext(t('login.browser_title', 'Prijava'));

  useEffect(() => {
    if (isAuthenticated) {
      // Že prijavljen obiskovalec, ki pride s povezave z javne tržnice, mora
      // priti do cilja prav tako — sicer bi bil edini, ki mu povezava ne dela.
      // T2529: brez `next` isti privzeti cilj kot obrazec in pot `/login`.
      // T2959: isto pravilo kot `LoginRoute` — obvezna 2FA ima prednost.
      navigate(afterLoginPath(currentUser, nextPath), { replace: true });
    }
  }, [isAuthenticated, currentUser, navigate, nextPath, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    setResendMessage('');
    setShowResend(false);
    setIsLoading(true);

    try {
      const response = await api.post('auth/login/', { email, password });
      if (response.data?.two_factor_required) {
        setPassword('');
        setTwoFactorToken(response.data.two_factor_token);
        return;
      }
      finishLogin(response.data.user);
    } catch (err) {
      const data = asApiError(err).response?.data;
      // T1902: zadeta mera (`login`, 5/min) je do zdaj padla v `else` in
      // izrisala DRF-jevo besedilo dobesedno — »Zahtevek je bil pridržan.
      // Expected available in 1 second.« Prijavna stran je bila edina javna
      // pot brez te veje, in hkrati najbolj obiskana.
      const omejitev = throttleMessage(err, t);
      // T2477: brez odgovora strežnika (ERR_CONNECTION_REFUSED) ali ob 5xx je
      // privzeta veja spodaj izpisala »Napačen email ali geslo.« — uporabnik je
      // začel ponastavljati pravilno geslo. Strežnik o vnosu ni odločil.
      const okvara = serverFailureMessage(err, t);
      if (omejitev) {
        setError(omejitev);
      } else if (okvara) {
        setError(okvara);
      } else if (data?.code === 'EMAIL_NOT_VERIFIED') {
        setShowResend(true);
        setError(t('login.error_email_not_verified'));
      } else if (data?.code === 'JOIN_REQUEST_PENDING') {
        setInfoMessage(t('login.join_request_pending'));
      } else if (data?.code === 'PASSWORD_NOT_SET') {
        setInfoMessage(t('login.password_not_set'));
      } else if (data?.code === 'INVALID_CREDENTIALS') {
        setError(t('login.error_invalid_credentials'));
      } else if (data?.code === 'ACCOUNT_INACTIVE') {
        setError(t('login.error_account_inactive'));
      } else if (data?.code === 'ACCOUNT_BLOCKED') {
        setError(t('login.error_account_blocked'));
      } else {
        setError(data?.error || t('login.error_invalid_credentials'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const finishLogin = (user: User, recoveryRemaining?: number) => {
    login(user);
    if (user.two_factor?.setup_required) {
      // T3000: `afterLoginPath` nese `next` v nastavitev 2FA — ista pot kot
      // `LoginRoute`, sicer zmaga tisti, ki teče zadnji (T2959).
      navigate(afterLoginPath(user, nextPath));
      return;
    }
    if (typeof recoveryRemaining === 'number') {
      toastCtx?.toast({
        type: 'info',
        title: t('two_factor.login_title'),
        message: t('two_factor.recovery_used', { count: recoveryRemaining }),
      });
    }
    navigate(nextPath);
  };

  const handleTwoFactorSuccess = ({ user, recovery_codes_remaining }: TwoFactorLoginResult) => {
    setTwoFactorToken(null);
    finishLogin(user, recovery_codes_remaining);
  };

  const handleResendEmail = async () => {
    setResendMessage('');
    setIsResending(true);
    try {
      const response = await api.post('auth/email-verify/resend/', { email });
      setResendMessage(response.data.message);
      setShowResend(false);
    } catch (err) {
      // Ista obravnava kot pri prijavi: ta pot ima svojo mero (`email_verify`,
      // 5/uro), torej je uporabnik zavrnjen dlje in sporočilo šteje toliko bolj.
      setError(
        throttleMessage(err, t) ??
          serverFailureMessage(err, t) ??
          asApiError(err).response?.data?.error ??
          t('common.error_generic', 'Prišlo je do napake.'),
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <WebsiteLogo className="h-9" />
        </div>
        <h2 className="mt-6 text-center text-display font-semibold text-foreground">{t('login.title', 'Prijava v račun')}</h2>
        <div className="mt-3">
          <EnvironmentBadge />
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-card py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-border relative">
          <div className="absolute top-3 right-3 z-[20]">
            <LanguageSwitcher variant="light" />
          </div>
          {twoFactorToken ? (
            <Suspense fallback={null}>
              <TwoFactorChallenge
                token={twoFactorToken}
                onSuccess={handleTwoFactorSuccess}
                onCancel={() => setTwoFactorToken(null)}
              />
            </Suspense>
          ) : (
          <form className="space-y-6" onSubmit={handleSubmit}>
            {regClosed && (
              <div className="bg-[var(--bp-accent-soft)] border-l-4 border-[var(--bp-accent)] p-4">
                <p className="text-body text-[var(--bp-accent-text)]">{t('login.registration_closed')}</p>
              </div>
            )}
            {infoMessage && (
              <div className="bg-[var(--bp-accent-soft)] border-l-4 border-[var(--bp-accent)] p-4">
                <p className="text-body text-[var(--bp-accent-text)]">{infoMessage}</p>
              </div>
            )}
            {resendMessage && (
              <div className="bg-green-50 dark:bg-green-950/40 border-l-4 border-green-400 dark:border-green-700 p-4">
                <p className="text-body text-green-700 dark:text-green-300">{resendMessage}</p>
              </div>
            )}
            {error && (
              <div className="bg-red-50 dark:bg-red-950/40 border-l-4 border-red-400 dark:border-red-700 p-4">
                <div className="flex flex-col gap-2">
                  <p className="text-body text-red-700 dark:text-red-300">{error}</p>
                  {showResend && (
                    <button
                      type="button"
                      onClick={handleResendEmail}
                      disabled={isResending}
                      className="text-dense font-semibold text-red-800 hover:text-red-900 dark:text-red-300 dark:hover:text-red-200 underline text-left disabled:opacity-50"
                    >
                      {isResending ? t('common.sending', 'Pošiljanje...') : t('login.resend_verification', 'Ponovno pošlji potrditveno e-pošto')}
                    </button>
                  )}
                </div>
              </div>
            )}
            
            <div>
              <label htmlFor="login-email" className="block text-body font-medium text-foreground">{t('common.email_label', 'E-naslov')}</label>
              <input
                id="login-email"
                type="email"
                required
                autoComplete="email"
                className="mt-1 block w-full px-3 py-2 bg-background text-foreground border border-input rounded-md text-body shadow-sm placeholder:text-muted-foreground
                           focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <PasswordInput
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              label={t('common.password_label', 'Geslo')}
            />


            <div className="flex items-center justify-between">
              <div className="text-body">
                <Link to="/forgot-password" className="font-medium text-primary hover:text-primary-hover">{t('login.forgot_password', 'Pozabljeno geslo?')}</Link>
              </div>
            </div>

            <div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full shadow-sm"
              >
                {isLoading ? t('common.logging_in', 'Prijavljanje...') : t('common.login', 'Prijavi se')}
              </Button>
            </div>
          </form>
          )}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-body">
                <span className="px-2 bg-card text-muted-foreground">{t('login.no_account', 'Še nimate računa?')}</span>
              </div>
            </div>

            <div className="mt-6">
              {isRegistrationOpen ? (
                <Link
                  to="/register"
                  className="w-full flex justify-center py-2 px-4 border border-border rounded-md shadow-sm text-body font-medium text-foreground bg-card hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  {t('login.create_account', 'Ustvari račun')}
                </Link>
              ) : (
                <Link
                  to="/waitlist"
                  className="w-full flex justify-center py-2 px-4 border border-border rounded-md shadow-sm text-body font-medium text-foreground bg-card hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  {t('login.join_waitlist', 'Prijavite se na čakalno listo')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
